<template>
  <div class="tool-detail-page white-theme pollo-layout">
    <div class="container">
      <section id="background-remover-tool" class="first-screen">
        <div class="breadcrumb">
          <router-link to="/">{{ $t('modelDetail.breadcrumb.home') }}</router-link>
          <span>&rsaquo;</span>
          <router-link to="/tools">{{ $t('tools.page.allTools') }}</router-link>
          <span>&rsaquo;</span>
          <span>{{ $t('tools.background-remover.name') }}</span>
        </div>

        <div class="first-screen-grid">
          <div class="action-column">
            <div v-if="!isLoggedIn" class="login-prompt">
              <div class="login-prompt-content">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" stroke-width="1"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                <h2>{{ $t('tools.detail.loginRequired') }}</h2>
                <p>{{ $t('tools.detail.loginRequiredDesc') }}</p>
                <button class="btn btn-primary" @click="goLogin">{{ $t('brandSeo.toolFreeTrialCta') }}</button>
              </div>
            </div>

            <div v-else class="tool-interface">
              <div class="upload-control-label">{{ $t('tools.background-remover.uploadLabel') }}<span>*</span></div>
              <div class="tool-upload" @click="triggerUpload" @dragover.prevent @drop.prevent="onDrop">
                <input type="file" ref="fileInput" accept="image/*" style="display:none" @change="handleFile">
                <div v-if="!uploaded" class="upload-placeholder">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" stroke-width="1"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/></svg>
                  <p>{{ $t('tools.background-remover.uploadHint') }}</p>
                </div>
                <div v-else class="upload-preview">
                  <div class="preview-box preview-box--image checkerboard-bg">
                    <img v-if="previewSrc" :src="previewSrc" :alt="$t('tools.background-remover.previewAlt')" class="preview-img clickable-preview" @click.stop="openImage(previewSrc)">
                  </div>
                  <button class="btn btn-secondary" @click.stop="clearUpload">{{ $t('tools.detail.reupload') }}</button>
                </div>
              </div>
              <p class="upload-spec">{{ $t('tools.background-remover.uploadSpec') }}</p>
              <div class="tool-options">
                <button class="btn btn-primary btn-lg" :disabled="!uploaded || processing" @click="process">
                  <svg v-if="processing" class="spinner" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                  <span>{{ processing ? $t('tools.detail.processing') : $t('tools.background-remover.processButton') }}</span>
                </button>
              </div>
            </div>
          </div>

          <div class="showcase-column">
            <div class="tool-hero">
              <div class="tool-hero-info">
                <h1>{{ $t('tools.background-remover.heroTitle') }}</h1>
                <p class="tool-hero-desc">{{ $t('tools.background-remover.fullDesc') }}</p>
              </div>
            </div>

            <div v-if="!processing && !processed" class="demo-section workspace-panel">
              <div class="demo-comparison">
                <div class="demo-item">
                  <span class="demo-label">{{ $t('tools.detail.before') }}</span>
                  <div class="demo-image"><img :src="demoBeforeSrc" :alt="$t('tools.background-remover.demoBeforeAlt')" class="clickable-preview" @click.stop="openImage(demoBeforeSrc)"></div>
                </div>
                <div class="demo-arrow"><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg></div>
                <div class="demo-item">
                  <span class="demo-label">{{ $t('tools.detail.after') }}</span>
                  <div class="demo-image checkerboard-bg"><img :src="demoAfterSrc" :alt="$t('tools.background-remover.demoAfterAlt')" class="clickable-preview" @click.stop="openImage(demoAfterSrc)"></div>
                </div>
              </div>
            </div>

            <div v-else-if="processing" class="processing-panel workspace-panel">
              <div class="workspace-heading"><h2>{{ $t('tools.detail.processing') }}</h2><span class="processing-status"><i></i>{{ $t('tools.background-remover.processingLabel') }}</span></div>
              <div class="processing-preview checkerboard-bg"><img v-if="previewSrc" :src="previewSrc" :alt="$t('tools.background-remover.processingAlt')"><div class="processing-overlay"><div class="loading-spinner"></div></div></div>
              <div class="processing-track"><span></span></div>
            </div>

            <div v-else class="result-panel workspace-panel">
              <div class="workspace-heading"><h2>{{ $t('tools.detail.after') }}</h2><span class="result-status">✓ {{ $t('tools.background-remover.resultLabel') }}</span></div>
              <div class="output-comparison">
                <div class="output-before"><span class="output-label">{{ $t('tools.detail.before') }}</span><div class="output-image output-image--fill"><img v-if="previewSrc" :src="previewSrc" :alt="$t('tools.background-remover.resultBeforeAlt')" class="compare-img clickable-preview" @click.stop="openImage(previewSrc)"></div></div>
                <div class="output-after"><span class="output-label">{{ $t('tools.detail.after') }}</span><div class="output-image output-image--fill checkerboard-bg"><img v-if="resultSrc" :src="resultSrc" :alt="$t('tools.background-remover.resultAfterAlt')" class="compare-img clickable-preview" @click.stop="openImage(resultSrc)"></div></div>
              </div>
              <div class="output-actions"><button v-if="resultSrc" type="button" class="btn btn-primary" @click="downloadResult">{{ $t('tools.detail.download') }}</button><button class="btn btn-secondary" @click="reset">{{ $t('tools.detail.processNew') }}</button></div>
            </div>
          </div>
        </div>
      </section>

      <div class="seo-content-start detail-section feature-section">
        <div class="section-heading">
          <span class="section-kicker">{{ $t('tools.background-remover.featuresKicker') }}</span>
          <h2>{{ $t('tools.background-remover.featuresTitle') }}</h2>
          <p>{{ $t('tools.background-remover.featuresIntro') }}</p>
        </div>
        <a v-if="features[0]" class="section-primary-cta" href="#background-remover-tool">{{ features[0].cta }}</a>
        <div class="landing-demo-comparison">
          <div class="landing-demo-half"><img :src="productBeforeSrc" :alt="$t('tools.background-remover.productBeforeAlt')" class="clickable-preview" @click.stop="openImage(productBeforeSrc)"></div>
          <div class="landing-demo-half checkerboard-bg"><img :src="productAfterSrc" :alt="$t('tools.background-remover.productAfterAlt')" class="clickable-preview product-cutout" @click.stop="openImage(productAfterSrc)"></div>
          <span class="landing-demo-divider"></span>
        </div>
        <div class="feature-list">
          <article v-for="(feature, idx) in features" :key="'feature-' + idx" class="feature-row">
            <div class="feature-visual">
              <div class="feature-visual-half"><img :src="featureImages[idx].before" :alt="featureImages[idx].beforeAlt" class="clickable-preview" @click.stop="openImage(featureImages[idx].before)"></div>
              <div class="feature-visual-half checkerboard-bg"><img :src="featureImages[idx].after" :alt="featureImages[idx].afterAlt" class="clickable-preview" @click.stop="openImage(featureImages[idx].after)"></div>
              <span class="feature-divider"></span>
            </div>
            <div class="feature-copy">
              <span class="feature-index">0{{ idx + 1 }}</span>
              <h3>{{ feature.title }}</h3>
              <p>{{ feature.desc }}</p>
              <a class="feature-cta" href="#background-remover-tool">{{ feature.cta }}</a>
            </div>
          </article>
        </div>
      </div>

      <div class="detail-section use-case-section">
        <div class="section-heading">
          <span class="section-kicker">{{ $t('tools.background-remover.useCasesKicker') }}</span>
          <h2>{{ $t('tools.background-remover.useCasesTitle') }}</h2>
          <p>{{ $t('tools.background-remover.useCasesIntro') }}</p>
        </div>
        <div class="use-case-grid">
          <article v-for="(item, idx) in useCases" :key="'case-' + idx" class="use-case-card">
            <span>0{{ idx + 1 }}</span>
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </article>
        </div>
      </div>

      <div class="detail-section how-to-section">
        <div class="section-heading">
          <span class="section-kicker">{{ $t('tools.background-remover.howToKicker') }}</span>
          <h2>{{ $t('tools.background-remover.howToTitle') }}</h2>
          <p>{{ $t('tools.background-remover.howToIntro') }}</p>
        </div>
        <div class="steps">
          <div v-for="(step, idx) in steps" :key="idx" class="step">
            <div class="step-number">{{ idx + 1 }}</div>
            <h3>{{ step.title }}</h3>
            <p>{{ step.desc }}</p>
          </div>
        </div>
      </div>

      <div class="detail-section more-tools-section">
        <h2>{{ $t('tools.detail.moreTools') }}</h2>
        <div class="grid-5"><ToolListCard v-for="t in otherTools" :key="t.id" :tool-id="t.id" :icon="t.icon" :badge="t.badge" :to="'/tools/' + t.id" /></div>
      </div>

      <div class="detail-section faq-section">
        <div class="section-heading"><h2>{{ $t('tools.background-remover.faqTitle') }}</h2></div>
        <div class="faq-list"><details v-for="(item, idx) in faqs" :key="'faq-' + idx" class="faq-item"><summary>{{ item.q }}</summary><p>{{ item.a }}</p></details></div>
      </div>

      <ToolHowToCta :kicker="$t('tools.background-remover.howToKicker')" :title="$t('tools.background-remover.howToTitle')" :intro="$t('tools.background-remover.howToIntro')" :steps="steps" :cta-label="$t('brandSeo.toolFreeTrialCta')" target-id="background-remover-tool" />
    </div>
    <ImageLightbox :src="lightboxSrc" @close="closeImage" />
  </div>
</template>

<script>
import { submitRemoveBg, pollRemoveBgResult } from '@/api/removeBg'
import { saveOrUpdateAIHistory, formatCreateDate, uploadFile } from '@/api'
import { isAuthenticated } from '@/api/auth'
import ImageLightbox from '@/components/ImageLightbox.vue'
import ToolListCard from '@/components/ToolListCard.vue'
import ToolHowToCta from '@/components/ToolHowToCta.vue'

var WATERMARK_MAX_BYTES = 15 * 1024 * 1024
var DEMO_BEFORE_SRC = require('@/assets/images/remove_background.png')
var DEMO_AFTER_SRC = require('@/assets/images/remove_background2.png')
var PRODUCT_BEFORE_SRC = require('@/assets/images/hub/background-remover-product-before.jpg')
var PRODUCT_AFTER_SRC = require('@/assets/images/hub/background-remover-product-after.png')
var ECOMMERCE_BEFORE_SRC = require('@/assets/images/hub/ecommerce-product-background-before.jpg')
var ECOMMERCE_AFTER_SRC = require('@/assets/images/hub/ecommerce-product-background-after.png')

export default {
  name: 'BackgroundRemoverDetail',
  components: {
    ImageLightbox: ImageLightbox,
    ToolListCard: ToolListCard,
    ToolHowToCta: ToolHowToCta
  },
  data() {
    return {
      uploaded: false,
      processing: false,
      processed: false,
      previewSrc: '',
      resultSrc: '',
      lightboxSrc: '',
      uploadedFile: null,
      processError: null,
      poller: null
    }
  },
  mounted() {
  },
  beforeDestroy() {
    if (this.poller) {
      this.poller.stop()
      this.poller = null
    }
  },
  computed: {
    isLoggedIn() {
      return !!this.$store.state.userInfo
    },
    otherTools() {
      return this.$store.state.tools.filter(function(t) { return t.id !== 'background-remover' }).slice(0, 5)
    },
    steps() {
      return this.$t('tools.background-remover.steps')
    },
    features() {
      return this.$t('tools.background-remover.features')
    },
    useCases() {
      return this.$t('tools.background-remover.useCases')
    },
    // 合并背景去除专属问答与 Miral AI 通用试用说明。
    faqs() {
      var toolFaqs = this.$t('tools.background-remover.faqs')
      var brandFaqs = this.$t('brandSeo.toolFaqs')
      return (Array.isArray(toolFaqs) ? toolFaqs : []).concat(Array.isArray(brandFaqs) ? brandFaqs : [])
    },
    demoBeforeSrc() {
      return DEMO_BEFORE_SRC
    },
    demoAfterSrc() {
      return DEMO_AFTER_SRC
    },
    productBeforeSrc() {
      return PRODUCT_BEFORE_SRC
    },
    productAfterSrc() {
      return PRODUCT_AFTER_SRC
    },
    featureImages() {
      return [
        { before: DEMO_BEFORE_SRC, after: DEMO_AFTER_SRC, beforeAlt: this.$t('tools.background-remover.featureAlts.portraitBefore'), afterAlt: this.$t('tools.background-remover.featureAlts.portraitAfter') },
        { before: PRODUCT_BEFORE_SRC, after: PRODUCT_AFTER_SRC, beforeAlt: this.$t('tools.background-remover.featureAlts.productBefore'), afterAlt: this.$t('tools.background-remover.featureAlts.productAfter') },
        { before: ECOMMERCE_BEFORE_SRC, after: ECOMMERCE_AFTER_SRC, beforeAlt: this.$t('tools.background-remover.featureAlts.ecommerceBefore'), afterAlt: this.$t('tools.background-remover.featureAlts.ecommerceAfter') }
      ]
    }
  },
  methods: {
    // 打开图片全屏预览。
    openImage(src) {
      this.lightboxSrc = src || ''
    },
    // 关闭图片全屏预览。
    closeImage() {
      this.lightboxSrc = ''
    },
    // 打开全局登录弹窗。
    goLogin() {
      this.$store.dispatch('showLoginModal')
    },
    // 唤起本地图片选择器。
    triggerUpload() {
      if (this.$refs.fileInput) this.$refs.fileInput.click()
    },
    // 接收拖拽到上传区的首个文件。
    onDrop(e) {
      var files = e.dataTransfer && e.dataTransfer.files
      if (files && files[0]) this.readFile(files[0])
    },
    // 接收文件选择器返回的图片。
    handleFile(e) {
      var input = e.target
      var file = input.files && input.files[0]
      if (!file) {
        if (input) input.value = ''
        return
      }
      this.readFile(file)
      if (input) input.value = ''
    },
    // 校验图片并读取为本地预览数据。
    readFile(file) {
      var self = this
      this.processError = null
      if (!file.type || file.type.indexOf('image/') !== 0) {
        this.processError = this.$t('tools.background-remover.notImageFile')
        return
      }
      if (file.size > WATERMARK_MAX_BYTES) {
        this.processError = this.$t('tools.background-remover.fileTooLarge')
        return
      }
      var reader = new FileReader()
      reader.onload = function(ev) {
        self.previewSrc = (ev.target && ev.target.result) ? String(ev.target.result) : ''
        self.uploadedFile = file
        self.uploaded = !!self.previewSrc
        self.processed = false
        self.resultSrc = ''
      }
      reader.onerror = function() {
        self.processError = self.$t('common.generateFailedContactSupport')
      }
      reader.readAsDataURL(file)
    },
    // 清空当前上传、处理和结果状态。
    clearUpload() {
      this.uploaded = false
      this.previewSrc = ''
      this.uploadedFile = null
      this.resultSrc = ''
      this.processed = false
      this.processing = false
      this.processError = null
    },
    // 重置工具以便处理下一张图片。
    reset() {
      this.clearUpload()
    },
    // 下载透明背景处理结果。
    downloadResult() {
      if (!this.resultSrc) return
      var url = this.resultSrc
      var name = this.$t('tools.background-remover.downloadName')
      if (url.indexOf('data:') === 0) {
        var a = document.createElement('a')
        a.href = url
        a.download = name
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        return
      }
      fetch(url)
        .then(function(r) { return r.blob() })
        .then(function(blob) {
          var u = URL.createObjectURL(blob)
          var a2 = document.createElement('a')
          a2.href = u
          a2.download = name
          document.body.appendChild(a2)
          a2.click()
          document.body.removeChild(a2)
          URL.revokeObjectURL(u)
        })
        .catch(function() {
          window.open(url, '_blank')
        })
    },
    // 上传原图、创建历史记录并提交背景去除任务。
    process() {
      var self = this
      if (!this.uploadedFile) return
      this.processing = true
      this.processed = false
      this.processError = null
      var createDate = formatCreateDate(new Date())
      var aiName = this.$t('tools.background-remover.name')
      var aiDesc = this.$t('tools.background-remover.name')
      var historyId = null
      var originalUrl = ''

      // 1. 先上传原图到服务器
      uploadFile(this.uploadedFile).then(function(url) {
        originalUrl = url
      }).catch(function() {
        self.processing = false
        self.processError = self.$t('tools.detail.uploadOriginalFailed')
        return Promise.reject('uploadOriginalFailed')
      }).then(function() {
        // 2. 创建历史记录，带上原图URL
        return saveOrUpdateAIHistory({
          aiName: aiName,
          taskId: '',
          aiDesc: aiDesc,
          state: 1,
          aiResultUrl: '',
          image_large_url: originalUrl,
          aiMediaType: 'image',
          createDate: createDate
        })
      }).then(function(hRes) {
        historyId = hRes && (hRes.historyId != null ? hRes.historyId : null)
      }).then(function() {
        // 3. 提交去背景任务
        return submitRemoveBg(self.uploadedFile)
      }).then(function(result) {
        // 兼容旧接口：同步直接返回 URL
        if (result && result.sync) {
          self.onRemoveBgSuccess(result.url, historyId, aiName, aiDesc, originalUrl, createDate)
          return
        }
        // 4. 轮询结果
        var createId = result
        self.poller = pollRemoveBgResult(createId, function(err, url) {
          self.poller = null
          if (err) {
            self.onRemoveBgFailed(err, historyId, aiName, aiDesc, originalUrl, createDate)
          } else {
            self.onRemoveBgSuccess(url, historyId, aiName, aiDesc, originalUrl, createDate)
          }
        })
      }).catch(function(err) {
        if (err === 'uploadOriginalFailed') return
        self.processing = false
        self.processed = false
        self.resultSrc = ''
        self.processError = self.$userFacingError(err, self.$t('common.generateFailedContactSupport'))
        saveOrUpdateAIHistory({
          aiName: aiName,
          historyId: historyId,
          taskId: '',
          aiDesc: aiDesc,
          state: 3,
          message: (err && err.message) ? err.message : '',
          aiResultUrl: '',
          image_large_url: originalUrl,
          aiMediaType: 'image',
          createDate: createDate
        }).catch(function() {})
      })
    },
    // 接收成功结果并同步更新生成历史。
    onRemoveBgSuccess(blobUrl, historyId, aiName, aiDesc, originalUrl, createDate) {
      var self = this
      self.processing = false
      self.resultSrc = blobUrl
      self.processed = true
      self.processError = null
      // 上传结果到服务器并更新历史记录
      fetch(blobUrl).then(function(r) { return r.blob() }).then(function(blob) {
        var file = new File([blob], 'background-removed.png', { type: blob.type || 'image/png' })
        return uploadFile(file)
      }).then(function(httpsUrl) {
        return saveOrUpdateAIHistory({
          aiName: aiName || self.$t('tools.background-remover.name'),
          historyId: historyId,
          taskId: '',
          aiDesc: aiDesc || self.$t('tools.background-remover.name'),
          state: 2,
          aiResultUrl: httpsUrl || '',
          image_large_url: originalUrl || '',
          aiMediaType: 'image',
          createDate: createDate || formatCreateDate(new Date())
        })
      }).catch(function() {
        /* 上传失败，仍用原始 URL 标记成功，确保后端记录更新为 state:2 */
        saveOrUpdateAIHistory({
          aiName: aiName || self.$t('tools.background-remover.name'),
          historyId: historyId,
          taskId: '',
          aiDesc: aiDesc || self.$t('tools.background-remover.name'),
          state: 2,
          aiResultUrl: blobUrl || '',
          image_large_url: originalUrl || '',
          aiMediaType: 'image',
          createDate: createDate || formatCreateDate(new Date())
        }).catch(function() {})
      })
    },
    // 处理失败状态并记录可追踪的错误信息。
    onRemoveBgFailed(err, historyId, aiName, aiDesc, originalUrl, createDate) {
      this.processing = false
      this.processed = false
      this.resultSrc = ''
      this.processError = this.$userFacingError(err, this.$t('common.generateFailedContactSupport'))
      saveOrUpdateAIHistory({
        aiName: aiName || this.$t('tools.background-remover.name'),
        historyId: historyId,
        taskId: '',
        aiDesc: aiDesc || this.$t('tools.background-remover.name'),
        state: 3,
        message: (err && err.message) ? err.message : '',
        aiResultUrl: '',
        image_large_url: originalUrl || '',
        aiMediaType: 'image',
        createDate: createDate || formatCreateDate(new Date())
      }).catch(function() {})
    }
  }
}
</script>

<style scoped>
@import '../styles/toolPolloLayout.css';
@import './ModelDetailStyles.css';

.tool-detail-page { background: #fff; overflow-x: hidden; }
.breadcrumb { display: flex; align-items: center; gap: 8px; color: #8a949e; font-size: 13px; }
.breadcrumb a { color: #65717c; }
.breadcrumb a:hover { color: #07977d; }
.login-prompt-content { max-width: 360px; margin: 0 auto; text-align: center; }
.login-prompt-content svg { display: block; margin: 0 auto; }
.login-prompt h2 { margin: 6px 0 4px; font-size: 20px; }
.login-prompt p { margin-bottom: 16px; color: #7c8792; font-size: 14px; }
.upload-control-label { margin-bottom: 10px; color: #303945; font-size: 13px; font-weight: 700; }
.upload-control-label span { margin-left: 3px; color: #f43f5e; }
.tool-upload { cursor: pointer; }
.upload-placeholder { display: flex; min-height: 230px; align-items: center; justify-content: center; flex-direction: column; padding: 36px 20px; border: 1.5px dashed #91d1c3; border-radius: 10px; background: #fff; transition: border-color .2s, background .2s; }
.upload-placeholder:hover { border-color: #0aa889; background: #f5fffb; }
.upload-placeholder p { margin-top: 12px; color: #4f5b66; font-size: 14px; }
.upload-spec { min-height: 34px; margin: 10px 0 0; color: #7b8791; font-size: 12px; line-height: 1.55; }
.upload-preview { display: flex; align-items: stretch; flex-direction: column; gap: 12px; }
.preview-box { display: flex; width: 100%; height: 260px; align-items: center; justify-content: center; overflow: hidden; border-radius: 10px; }
.preview-img { display: block; width: auto; max-width: 100%; height: auto; max-height: 100%; object-fit: contain; }
.clickable-preview { cursor: zoom-in; }
.checkerboard-bg { background-color: #f3f5f6; background-image: linear-gradient(45deg, #dfe4e7 25%, transparent 25%), linear-gradient(-45deg, #dfe4e7 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #dfe4e7 75%), linear-gradient(-45deg, transparent 75%, #dfe4e7 75%); background-position: 0 0, 0 8px, 8px -8px, -8px 0; background-size: 16px 16px; }
.tool-options { display: flex; margin-top: auto; padding-top: 18px; }
.tool-options .btn { width: 100%; justify-content: center; text-align: center; }
.tool-options .btn:disabled { cursor: not-allowed; opacity: .48; }
.tool-hero-info h1 { margin: 0 0 12px; color: #111827; font-size: 36px; line-height: 1.32; font-weight: 800; }
.tool-hero-desc { max-width: 680px; margin: 0 auto; color: #65717c; font-size: 15px; line-height: 1.7; }
.demo-section { padding: 0; border: 0; background: transparent; box-shadow: none; }
.demo-comparison { position: relative; display: grid; height: 406px; grid-template-columns: 1fr 1fr; gap: 2px; overflow: hidden; border-radius: 16px; background: #edf1f3; }
.demo-comparison::after { position: absolute; top: 0; bottom: 0; left: 50%; width: 2px; background: rgba(255, 255, 255, .94); content: ''; transform: translateX(-1px); }
.demo-item { position: relative; min-width: 0; overflow: hidden; }
.demo-label { position: absolute; z-index: 2; top: 14px; left: 50%; padding: 7px 14px; border-radius: 999px; background: rgba(20, 27, 34, .68); color: #fff; font-size: 11px; font-weight: 700; text-transform: uppercase; white-space: nowrap; transform: translateX(-50%); backdrop-filter: blur(8px); }
.demo-image { width: 100%; height: 100%; overflow: hidden; }
.demo-image img { width: 100%; height: 100%; object-fit: cover; object-position: center 24%; }
.demo-arrow { position: absolute; z-index: 3; top: 50%; left: 50%; display: flex; width: 38px; height: 38px; align-items: center; justify-content: center; border: 1px solid rgba(255, 255, 255, .9); border-radius: 50%; background: rgba(255, 255, 255, .92); color: #0b8871; transform: translate(-50%, -50%); box-shadow: 0 6px 18px rgba(18, 33, 42, .15); }
.workspace-heading { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.processing-status, .result-status { display: inline-flex; align-items: center; gap: 7px; padding: 6px 10px; border-radius: 999px; background: #eaf8f3; color: #087d68; font-size: 11px; font-weight: 700; }
.processing-status i { width: 7px; height: 7px; border-radius: 50%; background: #10b981; animation: pulse 1.2s ease-in-out infinite; }
.loading-spinner { width: 42px; height: 42px; border: 3px solid rgba(255, 255, 255, .28); border-top-color: #63e6be; border-radius: 50%; animation: spin .8s linear infinite; }
.output-comparison { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.output-label { display: block; margin-bottom: 7px; color: #7b8791; font-size: 11px; font-weight: 700; text-align: center; text-transform: uppercase; }
.output-image { display: flex; min-height: 320px; align-items: center; justify-content: center; overflow: hidden; border-radius: 12px; background: #edf1f3; }
.compare-img { display: block; width: 100%; height: 100%; object-fit: contain; }
.output-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 14px; }
.spinner { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
@keyframes pulse { 50% { opacity: .42; } }

.detail-section { max-width: 1180px; margin-right: auto; margin-bottom: 112px; margin-left: auto; }
.section-heading { max-width: 800px; margin: 0 auto 30px; text-align: center; }
.section-heading h2, .detail-section > h2 { margin: 8px 0 14px; color: #17212b; font-size: 40px; line-height: 1.4; }
.section-heading p { color: #697681; font-size: 16px; line-height: 1.8; }
.section-kicker { color: #07977d; font-size: 13px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
.section-primary-cta { display: table; min-width: 190px; margin: 0 auto 48px; padding: 13px 28px; border: 1px solid #0a9c82; border-radius: 999px; color: #087d68; font-size: 15px; font-weight: 750; text-align: center; }
.section-primary-cta:hover, .feature-cta:hover { color: #fff; background: #0a9c82; }
.landing-demo-comparison { position: relative; display: grid; width: min(880px, 100%); height: 500px; grid-template-columns: 1fr 1fr; margin: 0 auto 112px; overflow: hidden; border-radius: 18px; background: #edf1f3; box-shadow: 0 20px 55px rgba(31, 50, 63, .13); }
.landing-demo-half { display: flex; min-width: 0; align-items: center; justify-content: center; overflow: hidden; }
.landing-demo-half img { width: 100%; height: 100%; object-fit: cover; }
.landing-demo-half .product-cutout { width: 78%; height: 78%; object-fit: contain; }
.landing-demo-divider, .feature-divider { position: absolute; top: 0; bottom: 0; left: 50%; width: 2px; background: rgba(255, 255, 255, .94); transform: translateX(-1px); }
.feature-list { display: flex; flex-direction: column; gap: 96px; }
.feature-row { display: grid; grid-template-columns: minmax(0, 480px) minmax(320px, 480px); align-items: center; justify-content: space-between; gap: 72px; }
.feature-row:nth-child(even) .feature-visual { order: 2; }
.feature-row:nth-child(even) .feature-copy { order: 1; }
.feature-visual { position: relative; display: grid; height: 320px; grid-template-columns: 1fr 1fr; overflow: hidden; border-radius: 16px; background: #edf1f3; box-shadow: 0 20px 55px rgba(31, 50, 63, .13); }
.feature-visual-half { min-width: 0; overflow: hidden; }
.feature-visual-half img { width: 100%; height: 100%; object-fit: cover; }
.feature-copy { max-width: 480px; }
.feature-index { color: #07977d; font-size: 13px; font-weight: 800; letter-spacing: .12em; }
.feature-copy h3 { margin: 14px 0 16px; color: #18202a; font-size: 40px; line-height: 1.4; }
.feature-copy p { margin: 0 0 26px; color: #68737e; font-size: 16px; line-height: 1.75; }
.feature-cta { display: inline-flex; min-height: 46px; align-items: center; padding: 0 24px; border: 1px solid #0a9c82; border-radius: 999px; color: #087d68; font-size: 14px; font-weight: 750; }
.use-case-section { padding: 72px 56px; border-radius: 30px; background: #10282b; color: #fff; }
.use-case-section .section-kicker { color: #62e6bd; }
.use-case-section .section-heading h2 { color: #fff; }
.use-case-section .section-heading p { color: rgba(255, 255, 255, .68); }
.use-case-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
.use-case-card { padding: 26px; border: 1px solid rgba(255, 255, 255, .1); border-radius: 18px; background: rgba(255, 255, 255, .06); }
.use-case-card span { color: #62e6bd; font-size: 12px; font-weight: 800; }
.use-case-card h3 { margin: 14px 0 10px; color: #fff; font-size: 18px; }
.use-case-card p { color: rgba(255, 255, 255, .68); line-height: 1.7; }
.steps { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 24px; }
.step { min-height: 142px; padding: 30px; border-radius: 20px; background: #fff; text-align: center; box-shadow: 0 14px 40px rgba(29, 50, 63, .07); }
.step-number { display: flex; width: 44px; height: 44px; align-items: center; justify-content: center; margin: 0 auto 22px; border-radius: 14px; background: linear-gradient(135deg, #10b981, #2dd4bf); color: #fff; font-weight: 800; box-shadow: 0 8px 20px rgba(16, 185, 129, .22); }
.step h3 { margin-bottom: 6px; }
.step p { color: #7b8791; font-size: 14px; line-height: 1.7; }
.more-tools-section > h2 { margin-bottom: 44px; text-align: center; }
.more-tools-section .grid-5 { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.more-tools-section .grid-5 > :nth-child(n + 5) { display: none; }
.faq-section { max-width: 960px; margin-bottom: 48px; padding: 74px 36px; background: #fff; }
.faq-list { max-width: 860px; margin: 0 auto; }
.faq-item { margin: 0; padding: 0 8px; border-width: 0 0 1px; border-style: solid; border-color: #e7edf1; background: transparent; }
.faq-item summary { position: relative; cursor: pointer; padding: 23px 42px 23px 28px; font-weight: 750; list-style: none; }
.faq-item summary::-webkit-details-marker { display: none; }
.faq-item summary::after { position: absolute; top: 18px; left: 0; color: #53606b; font-size: 20px; content: '+'; }
.faq-item[open] summary::after { content: '−'; }
.faq-item p { padding: 0 42px 22px 28px; color: #697681; line-height: 1.7; }

@media (max-width: 1100px) {
  .feature-row { gap: 40px; }
}

@media (max-width: 768px) {
  .section-heading { margin-bottom: 24px; }
  .section-heading h2, .detail-section > h2 { font-size: 28px; line-height: 1.42; }
  .detail-section { margin-bottom: 76px; }
  .landing-demo-comparison { height: 320px; margin-bottom: 72px; border-radius: 12px; }
  .feature-list { gap: 68px; }
  .feature-row { grid-template-columns: 1fr; gap: 22px; text-align: center; }
  .feature-row:nth-child(even) .feature-visual, .feature-row:nth-child(even) .feature-copy { order: initial; }
  .feature-visual { height: 320px; }
  .feature-copy { max-width: none; }
  .feature-copy h3 { margin: 10px 0 12px; font-size: 24px; }
  .use-case-section { padding: 48px 18px; border-radius: 24px; }
  .use-case-grid, .steps { grid-template-columns: 1fr; }
  .more-tools-section .grid-5 { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
  .faq-section { margin-bottom: 32px; padding: 48px 0; }
  .output-comparison { grid-template-columns: 1fr; }
}
</style>
